import { useState } from "react";

export interface TrendPoint {
  label: string;
  collected: number;
  failed: number;
}

interface Props {
  data: TrendPoint[];
  height?: number;
}

const W = 900;
const H = 260;
const PAD_L = 40;
const PAD_R = 16;
const PAD_T = 16;
const PAD_B = 28;

export default function CollectionTrendChart({ data, height = H }: Props) {
  const [hover, setHover] = useState<number | null>(null);

  const n = data.length;
  if (n === 0) {
    return (
      <div className="flex h-full min-h-[220px] items-center justify-center text-xs text-ink-muted">
        No trend data available for this range
      </div>
    );
  }

  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const rawMax = Math.max(0, ...data.map((d) => d.collected + d.failed));
  const max = rawMax === 0 ? 4 : (rawMax <= 4 ? 4 : (rawMax <= 10 ? 10 : Math.ceil(rawMax * 1.15)));
  const yTicks = 5;
  const slot = innerW / n;
  const barW = Math.min(22, slot * 0.6);
  const barX = (i: number) => PAD_L + i * slot + (slot - barW) / 2;
  const yFor = (v: number) => PAD_T + innerH - (v / max) * innerH;
  const bottom = PAD_T + innerH;
  const labelStep = Math.max(1, Math.ceil(n / 6));

  const gridlines = Array.from({ length: yTicks }, (_, i) => {
    const ratio = i / (yTicks - 1);
    const val = Number.isInteger(max * ratio) ? max * ratio : Math.round(max * ratio);
    return { y: PAD_T + innerH - ratio * innerH, val };
  });

  const hoverP = hover != null ? data[hover] : null;
  const hoverLeft = hover != null ? ((barX(hover) + barW / 2) / W) * 100 : 0;
  const hoverTop = hoverP ? (yFor(hoverP.collected + hoverP.failed) / H) * 100 : 0;

  return (
    <div
      className="relative w-full"
      style={{ height }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        const px = ratio * W;
        if (px < PAD_L) {
          setHover(null);
          return;
        }
        const idx = Math.min(n - 1, Math.floor((px - PAD_L) / slot));
        setHover(idx);
      }}
      onMouseLeave={() => setHover(null)}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        role="img"
        aria-label="Collection trend over the last 30 days"
      >
        {gridlines.map((g) => (
          <g key={g.y}>
            <line x1={PAD_L} y1={g.y} x2={W - PAD_R} y2={g.y} stroke="#F1F5F9" strokeWidth="1" />
            <text x={PAD_L - 8} y={g.y + 4} textAnchor="end" className="fill-slate-400" fontSize="11">
              {g.val}
            </text>
          </g>
        ))}

        {data.map((d, i) => {
          const x = barX(i);
          const collectedTop = yFor(d.collected);
          const collectedH = bottom - collectedTop;
          const failedH = d.failed > 0 ? bottom - yFor(d.failed + d.collected) : 0;
          const failedTop = collectedTop - failedH;
          const hovered = hover === i;

          return (
            <g key={d.label + i}>
              {d.collected > 0 && (
                <rect
                  x={x}
                  y={collectedTop}
                  width={barW}
                  height={Math.max(2, collectedH)}
                  rx={d.failed > 0 ? 0 : 3}
                  fill={hovered ? "#047857" : "#059669"}
                />
              )}
              {d.failed > 0 && (
                <rect
                  x={x}
                  y={failedTop}
                  width={barW}
                  height={Math.max(2, failedH)}
                  rx={3}
                  fill={hovered ? "#b91c1c" : "#DC2626"}
                />
              )}
              {(i % labelStep === 0 || i === n - 1) && (
                <text
                  x={x + barW / 2}
                  y={H - 8}
                  textAnchor="middle"
                  className="fill-slate-400"
                  fontSize="11"
                >
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {hoverP && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-line bg-ink px-3 py-2 shadow-dropdown"
          style={{ left: `${hoverLeft}%`, top: `${hoverTop}%` }}
        >
          <p className="text-[11px] font-medium text-slate-300">{hoverP.label}</p>
          <p className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-white">
            <span className="h-2 w-2 rounded-full bg-[#059669]" />
            {hoverP.collected} collected
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-[13px] font-semibold text-white">
            <span className="h-2 w-2 rounded-full bg-[#DC2626]" />
            {hoverP.failed} failed
          </p>
        </div>
      )}
    </div>
  );
}